import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef, HostListener, OnDestroy, LOCALE_ID, Inject, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import * as moment from 'moment';
import { NgxMaskDirective } from 'ngx-mask';
import { Subject } from 'rxjs';
import { Globals } from 'src/app/app.global-variable';
import { DateTimeFormat } from 'src/app/core/constants/datetime-format-constants';

export const DATE_PICKER_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => OopsGemDatePickerComponent),
  multi: true,
};

@Component({
  selector: 'oops-gem-datepicker',
  templateUrl: './oops-gem-datepicker.component.html',
  styleUrls: ['./oops-gem-datepicker.component.scss'],
  providers: [DATE_PICKER_CONTROL_VALUE_ACCESSOR],
})
export class OopsGemDatePickerComponent implements OnDestroy, ControlValueAccessor {
  @Input() displayFormat = 'DD MMM YYYY';
  @Input() inputFormat = 'YYYY-MM-DD';
  @Input() firstDayOfWeek: number = 0;
  @Input() minDate: Date | moment.Moment | null = null;
  @Input() maxDate: Date | moment.Moment | null = null;
  @Input() calendarMidRange: boolean = false;
  @Input() options: any;
  @Input() width: any;
  @Input() height: any;
  @Input() inputWidth: any;
  @Input() displayPaddingTop: any = '9px';
  @Input() set openToDate(value: Date | moment.Moment | string | null) {
    if (!value) return;
    const date = this.toMomentWithInputFormat(value);
    if (!date.isSame(this._openToDate)) this._calendarOpened = false;
    if (this._calendarOpened) return;
    this.setViewDate(date);
    this._openToDate = date;
  }

  @Input("errorMessage") errorMessage: string;
  @Input("disabled")
  set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.showInput = false;
    }
  }

  @Input("showError")
  set showError(value: boolean) {
    this._showError = value;
  }
  @Input() displayBoxClass: string = '';
  @Input() iconColor: string = '';
  @Output() showErrorChange: EventEmitter<boolean> = new EventEmitter();

  @Input("data") set data(value: string | null) {
    let diff = Date.now() - this._lastEmitDateTime.getTime();
    if (diff < 100) return;

    this.writeValue(value);
  }
  @Output() dataChange: EventEmitter<string> = new EventEmitter();

  @Output() selectedDateChange = new EventEmitter<Date | string | null>();
  @Output() calendarPageChange = new EventEmitter<Date>();
  @ViewChild('dateinput') inputElement!: ElementRef;
  @ViewChild('calendarElement') calendarElement!: ElementRef;
  @ViewChild(NgxMaskDirective) maskDirective!: NgxMaskDirective;

  public selectedDate: moment.Moment | null = null;
  public viewDate: moment.Moment;
  public calendarDays: moment.Moment[][] = [];
  public showCalendar = false;
  public months: string[] = moment.months();
  public years: number[] = [];
  public currentMonth: number;
  public currentYear: number;
  public mask: string = '';
  public showInput = false;
  public inputPlaceHolder: string = 'YYYY-MM-DD';
  public isReturnDateFormat: boolean = true;
  public _disabled: boolean = false;
  public _showError: boolean = false;
  public _inputText: string = '';
  public _selectableClass: string = '';
  public _today: moment.Moment;

  private isCalendarFocused = false;
  private destroy$ = new Subject<void>();
  private onTouched: () => void = () => { };
  private onChanged: (value: any) => void = () => { };
  private isCustomDate: ((date: Date) => any) | null = null;

  private _lastEmitDateTime: Date = new Date('2000-01-01');
  private _calendarOpened: boolean = false;
  private _openToDate: moment.Moment = null;

  constructor(@Inject(LOCALE_ID) private locale: string,
    private globals: Globals
  ) {
    moment.locale('en');
    this._today = moment();
    this.viewDate = moment().startOf('month');
    this.currentMonth = this.viewDate.month();
    this.currentYear = this.viewDate.year();
  }

  ngAfterViewInit(): void {
    this.generateYears();
    this.gotoMaxOrMinDateViewDateInCondition();
    this.generateCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputFormat']) {
      this.setInput();
    }

    let isMinDateChanged: boolean = false;
    let isMaxDateChanged: boolean = false;
    let isOptionsChanged: boolean = false;

    if (changes['maxDate']) {
      isMaxDateChanged = true;
    }
    if (changes['minDate']) {
      isMinDateChanged = true;
    }

    if (changes['options'] && this.options) {
      isOptionsChanged = true;
      // support options configuration from bootstrap-daterangepicker
      if (this.options.isCustomDate && typeof this.options.isCustomDate === 'function') {
        this.isCustomDate = this.options.isCustomDate;
      }
      if (this.options.locale?.inputFormat) {
        this.inputFormat = this.options.locale.inputFormat;
        this.setInput();
      }
      if (this.options.inputFormat) {
        this.inputFormat = this.options.inputFormat;
        this.setInput();
      }
      if (this.options.locale?.displayFormat) {
        this.displayFormat = this.options.locale.displayFormat;
      }
      if (this.options.displayFormat) {
        this.displayFormat = this.options.displayFormat;
      }
      if (this.options.minDate) {
        const newMinDate = this.toDate(this.options.minDate);
        const oldMinDate = this.minDate ? this.toDate(this.minDate) : null;
        if (!oldMinDate || newMinDate.getTime() !== oldMinDate.getTime()) isMinDateChanged = true;
        this.minDate = this.options.minDate;
      }
      if (this.options.maxDate) {
        const newMaxDate = this.toDate(this.options.maxDate);
        const oldMaxDate = this.maxDate ? this.toDate(this.maxDate) : null;
        if (!oldMaxDate || newMaxDate.getTime() !== oldMaxDate.getTime()) isMaxDateChanged = true;
        this.maxDate = this.options.maxDate;
      }
      this.firstDayOfWeek = this.globals.FIRSTDAYOFWEEK;
      if (this.options.selectableClass) {
        this._selectableClass = this.options.selectableClass;
      }
      if (this.options.calendarMidRange !== undefined) {
        this.calendarMidRange = this.options.calendarMidRange;
      }
    }
    if (isMaxDateChanged || isMinDateChanged || isOptionsChanged) {
      this.generateYears();
      this.gotoMaxOrMinDateViewDateInCondition();
      this.generateCalendar();
    }
  }

  private setInput() {
    this.mask = this.getInputMaskFormat();
    this.inputPlaceHolder = this.getPlaceHolder();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getInputMaskFormat(): string {
    return this.inputFormat.replace(/Y/g, '0').replace(/M/g, '0').replace(/D/g, '0');
  }

  writeValue(value: any): void {
    if (value) {
      let momentValue = this.toMomentWithInputFormat(value);
      if (momentValue.isValid()) {
        this.selectedDate = momentValue;
        this.setInputTextBySelectedDate();
      } else {
        this.clearSelectedDate();
      }
    } else {
      this.clearSelectedDate();
    }
    this.setViewDate(this.selectedDate);
    this.generateCalendar();
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // implement interface of ControlValueAccessor
  }

  emitSelectedDate(): void {
    this._lastEmitDateTime = new Date();
    if (this.selectedDate) {
      const value = this.getSelectedDateData();
      this.onChanged(value);
      this.dataChange.emit(value);
      this.selectedDateChange.emit(value);
    } else {
      this.onChanged(null);
      this.dataChange.emit(null);
      this.selectedDateChange.emit(null);
    }
  }

  private getSelectedDateData(): any {
    if (this.isReturnDateFormat) {
      return this.selectedDate.format(DateTimeFormat.IsoDate);
    }
    else {
      return this.selectedDate.toDate();
    }
  }

  onInputChange(): void {
    const inputValue = this.inputElement.nativeElement.value;
    const parsedDate = this.toMomentWithInputFormat(inputValue);
    if (parsedDate.isValid()) {
      this.selectedDate = parsedDate;
      this.setViewDate(this.selectedDate);
      this.generateCalendar();
      this.emitSelectedDate();
    } else if (!inputValue || inputValue == this.getPlaceHolder()) {
      this.selectedDate = null;
      this.emitSelectedDate();
    }
    this._inputText = inputValue;
  }

  onKeyUp(event): void {
    if (event.key === "Enter") {
      if (this.selectedDate) {
        this.toggleDisplay();
      }
    }
    else if (event.key === "Escape") {
      this.toggleDisplay();
    }
    else {
      this.onInputChange();
    }
  }

  toggleInput(): void {
    if (this._disabled) return;
    this.showInput = true;
    this.showCalendar = true;
    this.isCalendarFocused = true;
    this.calendarPageChange.emit(this.viewDate.toDate());
    this.setInputTextBySelectedDate();
    setTimeout(() => {
      this.inputElement.nativeElement.focus();
    }, 100);
    this._calendarOpened = true;
  }

  onInputBlur(): void {
    setTimeout(() => {
      if (!this.isCalendarFocused) {
        this.toggleDisplay();
      }
    }, 100);
  }

  toggleDisplay(): void {
    this.showInput = false;
    this.showCalendar = false;
    this.setInputTextBySelectedDate();
  }

  toggleCalendar(event): void {
    event.stopPropagation();
    event.preventDefault();
    this.showCalendar = !this.showCalendar;
    if (this.showCalendar && !this.showInput) {
      this.showInput = true;
      setTimeout(() => {
        this.inputElement.nativeElement.focus();
      }, 100);
    } else if (!this.showCalendar && this.showInput) {
      this.showInput = false;
    }
  }

  clearCalendarClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.clearSelectedDate();
    this.emitSelectedDate();
  }

  private clearSelectedDate() {
    this.selectedDate = null;
    this._inputText = '';
    if (this.calendarMidRange) this.gotoMaxOrMinDateViewDateInCondition();
  }


  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showInput && !this.inputElement.nativeElement.contains(event.target as Node) && !this.calendarElement?.nativeElement.contains(event.target as Node)) {
      this.toggleDisplay();
    }
    if (this.inputElement.nativeElement.contains(event.target as Node)) {
      event.stopPropagation();
    }

  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    if (this.calendarElement?.nativeElement.contains(event.target as Node)) {
      this.isCalendarFocused = true;
    } else {
      this.isCalendarFocused = false;
    }
  }

  selectDate(date: moment.Moment): void {
    if (this.isDateSelectable(date)) {
      this.selectedDate = date;
      this.setInputTextBySelectedDate();
      this.emitSelectedDate();
      this.toggleDisplay();
    }
  }

  setInputTextBySelectedDate() {
    if (this.selectedDate) {
      this._inputText = this.selectedDate.format(this.inputFormat);
    } else {
      this._inputText = '';
    }
  }

  prevMonth(): void {
    const month = this.viewDate.month();
    if (month == 0) {
      const minYear = this.years[0];
      if (this.viewDate.year() == minYear) return;
    }

    this.isCalendarFocused = true;
    this.viewDate.subtract(1, 'month');
    this.currentMonth = this.viewDate.month();
    this.currentYear = this.viewDate.year();
    this.generateCalendar();
    this.calendarPageChange.emit(this.viewDate.toDate());
  }

  nextMonth(): void {
    const month = this.viewDate.month();
    if (month == 11) {
      const maxYear = this.years[this.years.length - 1];
      if (this.viewDate.year() == maxYear) return;
    }

    this.isCalendarFocused = true;
    this.viewDate.add(1, 'month');
    this.currentMonth = this.viewDate.month();
    this.currentYear = this.viewDate.year();
    this.generateCalendar();
    this.calendarPageChange.emit(this.viewDate.toDate());

  }

  selectMonth(monthIndex: number): void {
    this.isCalendarFocused = true;
    this.viewDate.month(monthIndex);
    this.currentMonth = this.viewDate.month();
    this.generateCalendar();
    this.calendarPageChange.emit(this.viewDate.toDate());
  }

  selectYear(year: number): void {
    this.isCalendarFocused = true;
    this.viewDate.year(year);
    this.currentYear = this.viewDate.year();
    this.generateCalendar();
    this.calendarPageChange.emit(this.viewDate.toDate());
  }

  generateCalendar(): void {
    this.calendarDays = [];
    const startOfMonth = this.viewDate.clone().startOf('month').startOf('week').add(this.firstDayOfWeek % 7, 'days');
    const endOfMonth = this.viewDate.clone().endOf('month').endOf('week').add(this.firstDayOfWeek % 7, 'days');
    let currentDate = startOfMonth.clone();

    while (currentDate.isBefore(endOfMonth, 'day') || currentDate.isSame(endOfMonth, 'day')) {
      this.calendarDays.push(Array.from({ length: 7 }, () => {
        const day = currentDate.clone();
        currentDate.add(1, 'day');
        return day;
      }));
    }
  }

  isDateSelectable(date: moment.Moment): boolean {
    if (this.minDate && date.isBefore(moment(this.minDate), 'day')) {
      return false;
    }
    if (this.maxDate && date.isAfter(moment(this.maxDate), 'day')) {
      return false;
    }
    if (this.isCustomDate && !this.isCustomDate(date.toDate())) {
      return false;
    }
    return true;
  }

  isSameDay(date1: moment.Moment | null, date2: moment.Moment): boolean {
    return date1 ? date1.isSame(date2, 'day') : false;
  }

  getSelectableClass(date: moment.Moment) {
    if (this.isSameDay(this._today, date)) return '';
    if (this.isDateSelectable(date)) {
      return this._selectableClass;
    }
    return '';
  }

  setViewDate(date: moment.Moment | null): void {
    if (date) {
      this.viewDate = date.clone().startOf('month');
      this.currentMonth = this.viewDate.month();
      this.currentYear = this.viewDate.year();
    }
  }

  generateYears(): void {
    const currentYear = moment().year();
    this.years = [];
    let start: number = currentYear - 120;
    let end: number = currentYear + 50;

    if (this.minDate) {
      start = this.toDate(this.minDate).getFullYear();
    }

    if (this.maxDate) {
      end = this.toDate(this.maxDate).getFullYear();
    }

    for (let i = start; i <= end; i++) {
      this.years.push(i);
    }
  }

  private toDate(value: any): Date {
    if (typeof value === 'string') {
      return new Date(value);
    }
    return value;
  }

  private toMomentWithInputFormat(value: any): moment.Moment {
    if (typeof value === 'string' && value.indexOf('T') > -1) {
      value = value.split('T')[0];
    }
    let momentValue = moment(value, this.inputFormat, true);
    if (!momentValue.isValid()) {
      momentValue = moment(value, 'YYYY-MM-DD', true);
    }
    if (momentValue.isValid()) {
      if (this.inputFormat.indexOf('YYYY') < 0 && this.maxDate) {
        let maxDate: Date;
        if (this.maxDate instanceof Date) {
          maxDate = this.maxDate;
        } else if (this.maxDate instanceof moment) {
          maxDate = this.maxDate.toDate();
        } else if (typeof this.maxDate === 'string') {
          maxDate = new Date(this.maxDate);
        }
        if (momentValue.year() > maxDate.getFullYear()) {
          const year = 1900 + Number(momentValue.year().toString().slice(-2));
          momentValue.year(year);
        }
      }
    }
    return momentValue;
  }


  private gotoMaxOrMinDateViewDateInCondition() {
    if (this.calendarMidRange) {
      if (this.setCalenarMidRanageView()) return;
    }
    let today = new Date();
    today.setHours(0, 0, 0, 0);
    if (this.minDate) {
      let minDate = this.toDate(this.minDate);
      if (minDate.getTime() > today.getTime()) {
        let mmMinDate = moment(minDate);
        if (this.viewDate.isBefore(mmMinDate, 'month')) {
          this.setViewDate(mmMinDate);
        }
      }
    }
    if (this.maxDate) {
      let maxDate = this.toDate(this.maxDate);
      if (maxDate.getTime() < today.getTime()) {
        let mmMaxDate = moment(maxDate);
        if (this.viewDate.isAfter(mmMaxDate, 'month')) {
          this.setViewDate(mmMaxDate);
        }
      }
    }
  }

  private setCalenarMidRanageView(): boolean {
    const min = moment(this.minDate);
    const max = moment(this.maxDate);
    if (min.isValid() && max.isValid()) {
      const maxYear = max.year();
      const minYear = min.year();
      const midYear = Math.floor((maxYear + minYear) / 2);

      const mid = moment();
      mid.set('year', midYear);
      this.setViewDate(mid);
      return true;
    }
    return false;
  }

  getWeekdaysShort() {
    let days = moment.weekdaysShort();
    let daysTemp = moment.weekdaysShort();
    for (let i = 0; i < this.firstDayOfWeek; i++) {
      let d = daysTemp[i];
      days.shift();
      days.push(d);
    }
    return days;
  }

  getPlaceHolder(): string {
    return this.inputFormat.toLowerCase();
  }
}
